import React, { useState, useEffect } from 'react';
import CustomerSection from './CustomerSection';
import LocationSection from './LocationSection';
import ImageUpload from './ImageUpload';
import { getDistanceLabel } from '../../../utils/distanceUtils';
import { formatDateUser, formatDateTimeUser, parseLocalDateTime } from '../../../utils/dateUtils';
import DateTimeInput from './DateTimeInput';
import { useAuth } from '../../../../../context/AuthContext';
import api from '../../../../../services/api';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import '../TaskModals.css';

const CreateTaskModal = ({ 
  isOpen,
  onClose,
  onSubmit,
  departments = [],
  employees = [],
  customers = [],
  employeeLocations = [],
  isSubmitting = false,
  initialTaskData = null,
  mode = 'create' // 'create' | 'edit'
}) => {
  const { company } = useAuth();
  
  const [taskForm, setTaskForm] = useState({
    description: '',
    customerId: '',
    departmentId: '',
    employeeId: '',
    supervisorId: '',
    priority: 'medium',
    dueDate: '',
    location: {
      coordinates: {
        lat: null,
        lng: null
      },
      address: ''
    },
    images: [],
    followUpOn: '',
    meetingDuration: 30
  });

  const [newCustomerMode, setNewCustomerMode] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({ 
    name: '', 
    email: '', 
    phone: '' 
  });
  
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isLoadingSupervisors, setIsLoadingSupervisors] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [meetingEnabled, setMeetingEnabled] = useState(false);

  const toLocalDateTimeInput = (value) => {
    try {
      if (!value) return '';
      // If already in local input format (YYYY-MM-DDTHH:mm)
      if (typeof value === 'string' && value.includes('T') && value.length >= 16 && !value.endsWith('Z')) {
        return value.slice(0, 16);
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      const tzOffsetMinutes = date.getTimezoneOffset();
      const localMillis = date.getTime() - tzOffsetMinutes * 60000;
      return new Date(localMillis).toISOString().slice(0, 16);
    } catch (_) {
      return '';
    }
  };

  useEffect(() => {
    if (company?.id && isOpen) {
      fetchSupervisors();
    }
  }, [company?.id, isOpen]);

  // Apply initialTaskData when opening the modal
  useEffect(() => {
    if (!isOpen) return;
    if (!initialTaskData) return;

    const isMeeting = typeof initialTaskData.taskType === 'string' && initialTaskData.taskType.toLowerCase() === 'meeting';

    const initialDepartmentId =
      initialTaskData.departmentId ||
      (initialTaskData.department && (initialTaskData.department.id || initialTaskData.department)) ||
      '';

    const initialEmployeeId =
      initialTaskData.employeeId ||
      (initialTaskData.assignedEmployee && initialTaskData.assignedEmployee.id) ||
      initialTaskData.employeeAssigned ||
      '';

    const initialSupervisorId =
      initialTaskData.supervisorId ||
      (initialTaskData.supervisor && initialTaskData.supervisor.id) ||
      initialTaskData.supervisorAssigned ||
      '';

    setTaskForm(prev => ({
      ...prev,
      description: initialTaskData.description ?? prev.description,
      customerId: initialTaskData.customerId || (initialTaskData.customer && initialTaskData.customer.id) || prev.customerId,
      departmentId: initialDepartmentId ? String(initialDepartmentId) : '',
      employeeId: initialEmployeeId ? String(initialEmployeeId) : '',
      supervisorId: initialSupervisorId ? String(initialSupervisorId) : '',
      dueDate: toLocalDateTimeInput(initialTaskData.dueDate) || prev.dueDate,
      followUpOn: initialTaskData.followUpOn ?? prev.followUpOn,
      meetingDuration: Number(initialTaskData.meetingDuration) || prev.meetingDuration || 30,
      location: initialTaskData.location ? {
        coordinates: {
          lat: initialTaskData.location.coordinates?.lat ?? prev.location.coordinates.lat,
          lng: initialTaskData.location.coordinates?.lng ?? prev.location.coordinates.lng
        },
        address: initialTaskData.location.address ?? prev.location.address
      } : prev.location,
      images: Array.isArray(initialTaskData.images) ? initialTaskData.images : prev.images
    }));
    setMeetingEnabled(isMeeting);
  }, [isOpen, initialTaskData]);

  useEffect(() => {
    if (taskForm.departmentId) {
      filterEmployeesByDepartment(taskForm.departmentId);
    } else {
      setFilteredEmployees([]);
    }
  }, [taskForm.departmentId, employees]);

  const fetchSupervisors = async () => {
    setIsLoadingSupervisors(true);
    try {
      const response = await api.get('/api/companies/users');
      if (response.data?.is_success) {
        const supervisorList = response.data.users.filter(user => 
          (user.role === 'admin' || user.role === 'supervisor') && user.isActive
        );
        setSupervisors(supervisorList);
      }
    } catch (error) {
      console.error('Error fetching supervisors:', error);
    } finally {
      setIsLoadingSupervisors(false);
    }
  };

  const filterEmployeesByDepartment = (departmentId) => {
    setIsLoadingEmployees(true);
    if (!departmentId) {
      setFilteredEmployees([]);
      setIsLoadingEmployees(false);
      return;
    }
    
    const filtered = employees.filter(emp =>
      emp.departments && emp.departments.some(dept => dept.id == departmentId)
    );
    setFilteredEmployees(filtered);
    setIsLoadingEmployees(false);
  };

  const getFilteredSupervisors = () => {
    if (!taskForm.departmentId) return [];
    
    return supervisors.filter(sup => {
      if (sup.role === 'admin') {
        return true;
      }
      return sup.departments && sup.departments.some(dept => 
        dept.id === parseInt(taskForm.departmentId)
      );
    });
  };

  const resetForm = () => {
    setTaskForm({
      description: '',
      customerId: '',
      departmentId: '',
      employeeId: '',
      supervisorId: '',
      priority: 'medium',
      dueDate: '',
      location: {
        coordinates: {
          lat: null,
          lng: null
        },
        address: ''
      },
      images: [],
      followUpOn: '',
      meetingDuration: 30
    });
    setNewCustomerMode(false);
    setNewCustomerData({ name: '', email: '', phone: '' });
    setValidationErrors({});
    setMeetingEnabled(false);
  };

  const handleSubmit = async () => {
    const errors = {};
    if (!taskForm.description.trim()) {
      errors.description = 'Description is required.';
    }

    if (!taskForm.departmentId) {
      errors.departmentId = 'Team is required.';
    }

    if (!taskForm.dueDate) {
      errors.dueDate = 'Due date/time is required.';
    } else {
      const currentDate = new Date();
      const dueDate = parseLocalDateTime(taskForm.dueDate);
      if (!dueDate) {
        errors.dueDate = 'Invalid due date/time.';
      } else if (dueDate <= currentDate) {
        errors.dueDate = 'Due date/time must be in the future.';
      }
    }

    // Location is optional, even when assigning field staff.

    // Customer is optional. If adding a new customer, require minimum fields.
    if (newCustomerMode) {
      if (!newCustomerData.name.trim()) {
        errors.newCustomerName = 'Customer name is required.';
      }
      if (!newCustomerData.email.trim() && !newCustomerData.phone.trim()) {
        errors.newCustomerEmail = 'Provide customer email or phone.';
      }
    }

    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    // Prepare customer data
    let customerData = null;
    if (newCustomerMode) {
      customerData = newCustomerData;
    } else if (taskForm.customerId) {
      // If an existing customer is selected, include details (optional overall)
      const selectedCustomer = customers.find(customer => customer.id === parseInt(taskForm.customerId));
      if (selectedCustomer) {
        customerData = {
          name: selectedCustomer.name,
          email: selectedCustomer.email,
          phone: selectedCustomer.phone || ''
        };
      }
    }

    const submitData = {
      ...taskForm,
      followUpOn: taskForm.followUpOn || null,
      customer: customerData,
      taskType: meetingEnabled ? 'meeting' : 'task'
    };
    // For meetings, include participants (emails) based on selected entities
    if (meetingEnabled) {
      submitData.meetingDuration = Number(taskForm.meetingDuration) || 30;
      const participantEmails = [];
      // Supervisor email
      const sup = supervisors.find(s => String(s.id) === String(taskForm.supervisorId));
      if (sup?.email) participantEmails.push(sup.email);
      // Employee email
      const emp = employees.find(e => String(e.id) === String(taskForm.employeeId));
      if (emp?.email) participantEmails.push(emp.email);
      // Customer email
      if (customerData?.email) participantEmails.push(customerData.email);
      if (participantEmails.length > 0) {
        submitData.participants = participantEmails;
      }
    }

    const result = await onSubmit(submitData);
    if (result.success) {
      resetForm();
      onClose();
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Lock body scroll when modal is open
  useBodyScrollLock(isOpen);

  // Close on ESC
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', onKeyDown);
    }
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  if (!isOpen) return null;

  const filteredSupervisors = getFilteredSupervisors();
  const supervisorsToShow = taskForm.departmentId ? filteredSupervisors : supervisors;
  const employeesToShow = taskForm.departmentId ? filteredEmployees : employees;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const headerChips = [
    { label: 'Priority', value: taskForm.priority.charAt(0).toUpperCase() + taskForm.priority.slice(1) },
    taskForm.dueDate ? { label: 'Due Date', value: formatDateTimeUser(taskForm.dueDate) } : null,
    taskForm.followUpOn ? { label: 'Follow Up', value: formatDateTimeUser(taskForm.followUpOn) } : null
  ].filter(Boolean);

  return (
    <div className="modal-overlay task-modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content task-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header task-modal-header">
          <div className="task-modal-header-content">
            <div className="task-title-row">
              <span className="task-id-pill">{mode === 'edit' ? 'Edit Task' : 'Create Task'}</span>
            </div>
            {!!headerChips.length && (
              <div className="task-meta-row">
                {headerChips.map((chip) => (
                  <div key={chip.label} className="task-meta-chip">
                    <span className="chip-label">{chip.label}</span>
                    <span className="chip-value">{chip.value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button className="close-button" onClick={handleClose} aria-label="Close" title="Close">Close</button>
        </div>

        <div className="modal-body task-modal-body">
          <div className="form-row">
            <div className="form-group full-width">
              <label htmlFor="description">Description<span className="required-asterisk">*</span></label>
              <textarea
                id="description"
                className={`form-control${validationErrors.description ? ' error' : ''}`}
                value={taskForm.description}
                onChange={(e) => {
                  if (validationErrors.description) setValidationErrors(prev => ({ ...prev, description: undefined }));
                  setTaskForm(prev => ({ ...prev, description: e.target.value }));
                }}
                rows="3"
                placeholder="Enter task description"
                autoFocus
                required
              />
              {validationErrors.description && (
                <div className="field-error-message">{validationErrors.description}</div>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width">
             {/*  <label>Priority & Timing</label> */}
              <div className="combined-inputs three-cols">
                <div className="mini-field">
                  <label htmlFor="priority">Priority</label>
                  <select
                    id="priority"
                    className="form-control"
                    value={taskForm.priority}
                    onChange={(e) => setTaskForm(prev => ({ ...prev, priority: e.target.value }))}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
                <div className="mini-field">
                  <DateTimeInput
                    id="dueDate"
                    label="Due Date"
                    required
                    error={validationErrors.dueDate}
                    value={taskForm.dueDate}
                    min={toLocalDateTimeInput(new Date())}
                    stepSeconds={60}
                    onClearError={() => {
                      if (validationErrors.dueDate) setValidationErrors(prev => ({ ...prev, dueDate: undefined }));
                    }}
                    onChange={(val) => setTaskForm(prev => ({ ...prev, dueDate: val }))}
                  />
                 
                  {validationErrors.dueDate && (
                    <div className="field-error-message">{validationErrors.dueDate}</div>
                  )}
                </div>
                <div className="mini-field">
                  <DateTimeInput
                    id="followUpOn"
                    label="Follow Up On"
                    value={taskForm.followUpOn}
                    min={toLocalDateTimeInput(new Date())}
                    stepSeconds={60}
                    onChange={(val) => setTaskForm(prev => ({ ...prev, followUpOn: val }))}
                  />
                
                </div>
              </div>
              <div style={{ marginTop: 6, display: 'flex', gap: 12, color: '#6b7280', fontSize: 12 }}>
              <span>Set a due date and optional follow-up reminder. {meetingEnabled ? 'A calendar invite with a Google Meet link will be created.' : ''}</span>
              </div>
            
            {meetingEnabled && (
              <div className="combined-inputs" style={{ marginTop: 8 }}>
                <div className="mini-field" /* style={{ maxWidth: 360 }} */>
                  <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>
                    Meeting Duration
                  </span>
                  <div style={{ display: 'flex', gap: 5, overflowX: 'auto', maxWidth: '100%', WebkitOverflowScrolling: 'touch', paddingBottom: 2 }}>
                    {[15, 30, 45, 60].map((mins) => {
                      const isActive = Number(taskForm.meetingDuration) === mins;
                      return (
                        <button
                          key={mins}
                          type="button"
                          aria-pressed={isActive}
                          onClick={() => setTaskForm(prev => ({ ...prev, meetingDuration: mins }))}
                          style={{
                            padding: '5px 5px',
                            borderRadius: 5,
                            border: isActive ? '1px solid #0f172a' : '1px solid rgba(148,163,184,0.5)',
                            background: isActive ? '#0f172a' : '#ffffff',
                            color: isActive ? '#ffffff' : '#0f172a',
                            fontSize: 12,
                            fontWeight: 500,
                            cursor: 'pointer'
                          }}
                        >
                          {mins} min
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
              <input
                id="create-meet"
                type="checkbox"
                style={{ width: 16, height: 16, marginRight: 5 }}
                checked={meetingEnabled}
                onChange={(e) => setMeetingEnabled(e.target.checked)}
              />
              <span
                onClick={() => setMeetingEnabled(prev => !prev)}
                style={{ userSelect: 'none', textTransform: 'none', fontSize: 12, color: '#6b7280', cursor: 'pointer' }}
              >
                Create Google Meet (includes supervisor/ field staff and customer)
              </span>
            </div>
              
            </div>
          </div>

          <div className="form-row">
            <CustomerSection 
              taskForm={taskForm}
              setTaskForm={setTaskForm}
              customers={customers}
              newCustomerMode={newCustomerMode}
              setNewCustomerMode={setNewCustomerMode}
              newCustomerData={newCustomerData}
              setNewCustomerData={setNewCustomerData}
              validationErrors={{ newCustomerName: validationErrors.newCustomerName, newCustomerEmail: validationErrors.newCustomerEmail }}
            />

            <div className="form-group">
              <label htmlFor="team">Assignment</label>
              <div className="combined-inputs">
                <select
                  id="team"
                  className={`form-control${validationErrors.departmentId ? ' error' : ''}`}
                  value={taskForm.departmentId}
                  onChange={(e) => {
                    const departmentId = e.target.value;
                    if (validationErrors.departmentId) setValidationErrors(prev => ({ ...prev, departmentId: undefined }));
                    setTaskForm(prev => ({ 
                      ...prev, 
                      departmentId,
                      employeeId: '',
                      supervisorId: ''
                    }));
                  }}
                >
                  <option value="">Select Team *</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
                {validationErrors.departmentId && (
                  <div className="field-error-message">{validationErrors.departmentId}</div>
                )}

                {!!taskForm.departmentId && (
                  <>
                    <select
                      id="supervisor"
                      className="form-control"
                      value={taskForm.supervisorId}
                      onChange={(e) => setTaskForm(prev => ({ ...prev, supervisorId: e.target.value }))}
                      disabled={isLoadingSupervisors}
                      style={{ color: '#222', background: '#fff' }}
                    >
                      <option value="">
                        {isLoadingSupervisors ? "Loading supervisors..." : "Supervisor (optional)"}
                      </option>
                      {supervisorsToShow.map(supervisor => (
                        <option key={supervisor.id} value={supervisor.id}>
                          {supervisor.firstName} {supervisor.lastName}
                          {supervisor.role === 'admin' ? ' (Admin)' : ''}
                        </option>
                      ))}
                    </select>

                    <select
                      id="employee"
                      className="form-control"
                      value={taskForm.employeeId}
                      onChange={(e) => setTaskForm(prev => ({ ...prev, employeeId: e.target.value }))}
                      disabled={isLoadingEmployees}
                      style={{ color: '#222', background: '#fff' }}
                    >
                      <option value="">
                        {isLoadingEmployees ? "Loading employees..." : "Field Staff (optional)"}
                      </option>
                      {employeesToShow.map(employee => (
                        <option key={employee.id} value={employee.id}>
                          {employee.firstName} {employee.lastName} {getDistanceLabel(employee, taskForm.location, employeeLocations)}
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>
              {!isLoadingSupervisors && filteredSupervisors.length === 0 && taskForm.departmentId && (
                <small className="form-text text-muted">No supervisors found for this team</small>
              )}
              {!isLoadingEmployees && filteredEmployees.length === 0 && taskForm.departmentId && (
                <small className="form-text text-muted">No active employees found for this team</small>
              )}
            </div>
          </div>

          <div className="form-row">
            <LocationSection 
              taskForm={taskForm}
              setTaskForm={setTaskForm}
            validationErrors={{ location: validationErrors.location }}
            isRequired={false}
            />

            <ImageUpload 
              taskForm={taskForm}
              setTaskForm={setTaskForm}
              isSubmitting={isSubmitting}
            />
          </div>
        </div>

        <div className="modal-footer task-modal-footer">
          <button
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button 
            className="submit-button"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (mode === 'edit' ? 'Updating...' : 'Creating...') : (mode === 'edit' ? 'Update Task' : 'Create Task')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateTaskModal; 